<template>
  <div id="product-distribution-grid" class="d-flex flex-column h-100 w-100">
    <AgGridVue
      class="w-100 h-100"
      :row-height="20"
      :header-height="30"
      :column-defs="columnDefs"
      :row-data="productDistributionByCluster"
      :grid-options="gridOptions"
      :status-bar="false"
      @grid-ready="onGridReady"
      @column-visible="resizeClusters"
    />
  </div>
</template>

<script>
import { filter, get, keyBy, keys, map, partition } from 'lodash';
import { mapActions, mapState } from 'pinia';

// composables
import { useInternationalization } from '@/composables/use-internationalization';
import { useFormatNumber } from '@/composables/use-format-number';

// stores
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useClusteringStore } from '@/stores/clustering';
import { useContextStore } from '@/stores/context';
import { useFurnitureStore } from '@/stores/furniture';

// mixins
import productMixin from '../product.mixin'; // TODO remove

const MAX_VISIBLE_COLUMNS = 4;
const UNCLUSTERED_ID = 'unclustered';
export default {
  mixins: [productMixin], // TODO remove

  setup() {
    const { tkey } = useInternationalization(
      'assortmentCanvasPage.productDashboard.productDistribution'
    );
    const { formatNumber } = useFormatNumber();

    return { tkey, formatNumber };
  },

  data() {
    return {
      gridApi: null,
      productDistributionByCluster: [],
      columnDefs: [],
      gridOptions: {
        columnMenu: 'legacy',
        defaultColDef: {
          filter: false,
          sortable: false,
          editable: false,
          // menu to hide/show columns
          menuTabs: ['columnsMenuTab'],
          suppressMenuHide: true,
          suppressMovable: true,
          suppressToolPanel: false,
          minWidth: 100,
          flex: 1,
          // wraps value in a span that can be styled with ellipsis for overflow
          // normal cell container is a flex item which doesn't support ellipsis
          cellRenderer(params) {
            if (!params.value) return;
            return `<span title="${params.value}">${params.value}</span>`;
          },
        },
        getRowClass: params => {
          return params.node.rowIndex === 2 ? 'ag-header separator' : '';
        },
        onRowDataUpdated: params => {
          params.api.autoSizeColumns(['rowCategory']);
        },
        suppressMenuHide: true,
        alwaysShowVerticalScroll: true,
        suppressHorizontalScroll: false,
      },
      rowsCategories: [
        {
          field: 'capturedInPercentageOfTransactions',
          text: 'capturedInPercentageOfTransactions',
          formatNumber: true,
          isPercentage: true,
        },
        {
          field: 'soldInPercentageOfROS',
          text: 'soldInPercentageOfROS',
          formatNumber: true,
          isPercentage: true,
        },
        {
          field: 'soldInPercentageOfStores',
          text: 'soldInPercentageOfStores',
          formatNumber: true,
          isPercentage: true,
        },
        { field: 'optimisedRank', text: 'optimisedRank' },
        { field: 'dataSource', text: 'dataSource' },
        { field: 'utility', text: 'utility' },
        { field: 'rateOfSale', formatNumber: true },
        {
          field: 'optimisedAccumulatedSalesWhenCut',
          text: 'optimisedAccumulatedSalesWhenCut',
          formatNumber: true,
        },
        { field: 'volume', text: 'volume', formatNumber: true },
        { field: 'margin', text: 'margin', formatMarginPercentage: true },
        { field: 'positionLatestEdit', text: 'positionLatestEdit' },
      ],
      distributionColumn: {
        headerName: '',
        colId: 'rowCategory',
        field: 'rowCategory',
        cellClass: 'pl-5',
        pinned: 'left',
        lockVisible: true,
        // this column cannot be unselected
        suppressColumnsToolPanel: true,
        cellStyle: { 'font-weight': 'bold' },
        suppressSizeToFit: true,
      },
      invisibleColumns: [],
      visibleColumns: [],
    };
  },

  computed: {
    ...mapState(useClusteringStore, ['selectedScheme', 'selectedClusterScheme']),
    ...mapState(useAssortmentCanvasStore, [
      'selectedPod',
      'getIndexedCanvasProducts',
      'getDashboardProduct',
      'canvasesById',
      'canvases',
    ]),
    ...mapState(useFurnitureStore, ['getSpacebreaksIndexedById']),
    ...mapState(useContextStore, ['getCurrentNumericLocale']),

    currentNumericLocale() {
      return this.getCurrentNumericLocale;
    },

    requiredCurrency() {
      return this.$t(`currencies.${this.currentNumericLocale}`);
    },

    unclusteredHeader() {
      return {
        headerName: this.tkey('tableHeaders.unclustered'),
        suppressSizeToFit: true,
        field: UNCLUSTERED_ID,
      };
    },
  },

  watch: {
    getDashboardProduct: {
      immediate: true,
      handler(newProduct, oldProduct) {
        this.resetColumnsVisibility();
        if (!newProduct || !!(oldProduct && newProduct.productKey === oldProduct.productKey))
          return;
        this.fetchProductInformation(newProduct.productKey);
      },
    },
  },

  created() {
    this.setTableColumns();
  },

  methods: {
    ...mapActions(useAssortmentCanvasStore, ['fetchProductDistribution']),

    onGridReady(params) {
      this.gridApi = params.api;
    },

    setTableColumns() {
      // Get column info for each cluster. The currently selected cluster is the first column to be added
      const productCluster = this.canvasesById[this.getDashboardProduct.canvasId];
      const clusterColumns = get(this.selectedClusterScheme, 'clusters', []).map(
        (c, columnIndex) => {
          const hide = columnIndex + 1 > MAX_VISIBLE_COLUMNS - 1; // -1 considering unclustered
          (hide ? this.invisibleColumns : this.visibleColumns).push(c.clusterId);
          return {
            headerName: c.clusterName,
            field: c.clusterId,
            minWidth: 140,
            hide,
            suppressSizeToFit: false,
          };
        }
      );

      const allColumns = [this.unclusteredHeader, ...clusterColumns];
      const [[firstColumn], remainingColumns] = partition(allColumns, {
        field: productCluster.clusterId || UNCLUSTERED_ID,
      });

      const formattedColumns = [this.distributionColumn, firstColumn, ...remainingColumns].map(
        (c, columnIndex) => {
          c.cellStyle = c.cellStyle || {};
          c.menuTabs = [];

          // first cluster entry
          if (columnIndex === 1) {
            c.lockVisible = true;
            c.cellStyle['font-weight'] = 'bold';
            c.pinned = 'left';
            c.maxWidth = 150;
            // this column cannot be unselected
            c.suppressColumnsToolPanel = true;
          }

          return c;
        }
      );

      if (clusterColumns.length > MAX_VISIBLE_COLUMNS - 1) {
        formattedColumns.push({
          colId: 'menuIcon',
          headerName: '',
          width: 30,
          maxWidth: 30,
          menuTabs: ['columnsMenuTab'],
          // TODO this class is set only to provide deep overrides
          // check if we can remove it
          headerClass: 'actions-column pa-0 pr-2',
          pinned: 'right',
          icons: { menu: '...' },
          // this column cannot be unselected
          suppressColumnsToolPanel: true,
          lockVisible: true,
        });
      }

      this.columnDefs = formattedColumns;
    },

    getCellData(c, p, clusterId) {
      if (c.field === 'dataSource') {
        return this.$t(`assortmentCanvas.${p.isNewProduct ? 'modelled' : 'measured'}`);
      }
      if (c.field === 'positionLatestEdit') {
        const pod = this.podAndStoreCounts(
          p,
          clusterId,
          this.getSpacebreaksIndexedById[clusterId],
          {}
        ).currentSpacebreak;
        return this.$t('assortmentCanvas.productPositioning', [
          pod.shortName,
          pod.storeCount,
          pod.pod,
        ]);
      }

      let rowData = p[c.field];

      if (c.formatNumber) {
        rowData = this.formatNumber({ number: rowData || 0, format: 'float' });
      }

      if (c.isPercentage) {
        return `${rowData}%`;
      }

      if (c.formatMarginPercentage) {
        const formattedMargin = Number(rowData * 100)
          .toFixed(0)
          .toLocaleString(this.currentNumericLocale);
        return `${formattedMargin}%`;
      }

      return rowData;
    },

    buildTableContents(clusteredData) {
      const productByCanvas = keyBy(clusteredData, 'canvasId');

      this.productDistributionByCluster = this.rowsCategories.map(category => {
        let rowCategoryText = this.$t(`assortmentCanvas.${category.text}`);
        if (category.field === 'rateOfSale') {
          rowCategoryText = this.$t('assortmentCanvas.sales', { currency: this.requiredCurrency });
        }
        return {
          rowCategory: rowCategoryText,
          ...keys(productByCanvas).reduce((acc, canvasId) => {
            const product = productByCanvas[canvasId];
            const clusterId = this.canvasesById[canvasId].clusterId;
            return {
              ...acc,
              [clusterId || UNCLUSTERED_ID]: this.getCellData(category, product, clusterId),
            };
          }, {}),
        };
      });
    },

    async fetchProductInformation(productKey) {
      this.productDistributionByCluster = [];
      const canvasesInStoreClass = map(
        filter(this.canvases, { storeClassId: this.getDashboardProduct.storeClassId }),
        '_id'
      );
      const clusteredData = await this.fetchProductDistribution({
        productKey,
        canvases: canvasesInStoreClass,
      });

      this.buildTableContents(clusteredData);
    },

    resetColumnsVisibility() {
      if (this.invisibleColumns.length && this.gridApi) {
        this.gridApi.setColumnsVisible(this.visibleColumns, true);
        this.gridApi.setColumnsVisible(this.invisibleColumns, false);
      }
    },
    resizeClusters() {
      this.gridApi.sizeColumnsToFit();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';
:deep(.actions-column) {
  width: 30px !important;
  & span {
    color: $assortment-primary-colour;
    font-weight: bold;
    font-size: large;
  }
}
</style>
